import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { PALETTE } from "../palette";

const MOTE_COUNT = 60;
const SPREAD_X = 8;
const SPREAD_Z = 6;
const HEIGHT_RANGE = 3;
const DRIFT_SPEED = 0.15;

/**
 * A cheap stand-in for volumetric light shafts (M1a): slow-drifting
 * additive-blended points, brightened by bloom rather than simulated as
 * real light scattering.
 */
export function DustMotes() {
  const pointsRef = useRef<THREE.Points>(null);

  const { positions, seeds } = useMemo(() => {
    const positions = new Float32Array(MOTE_COUNT * 3);
    const seeds = new Float32Array(MOTE_COUNT);
    for (let i = 0; i < MOTE_COUNT; i++) {
      positions[i * 3 + 0] = (Math.random() - 0.5) * SPREAD_X + 3.5;
      positions[i * 3 + 1] = Math.random() * HEIGHT_RANGE;
      positions[i * 3 + 2] = (Math.random() - 0.5) * SPREAD_Z + 2.5;
      seeds[i] = Math.random() * Math.PI * 2;
    }
    return { positions, seeds };
  }, []);

  useFrame(({ clock }) => {
    const points = pointsRef.current;
    if (!points) return;
    const posAttr = points.geometry.attributes.position as THREE.BufferAttribute;
    const t = clock.elapsedTime;
    for (let i = 0; i < MOTE_COUNT; i++) {
      const y = posAttr.getY(i) + DRIFT_SPEED * 0.01;
      const wrappedY = y > HEIGHT_RANGE ? 0 : y;
      const sway = Math.sin(t * 0.3 + seeds[i]!) * 0.003;
      posAttr.setY(i, wrappedY);
      posAttr.setX(i, posAttr.getX(i) + sway);
    }
    posAttr.needsUpdate = true;
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        color={PALETTE.hazardSpark}
        size={0.03}
        transparent
        opacity={0.5}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </points>
  );
}
